import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useDeleteImage } from '@services/flats/mutations/useDeleteImage'
import { useUpdateGalleryOrder } from '@services/flats/mutations/useUpdateGalleryOrder'
import { useUploadFlatImage } from '@services/flats/mutations/useUploadFlatImage'
import { SNA_IMAGE_PREFIX } from '@services/utils'
import { useQueryClient } from '@tanstack/react-query'
import { GripVertical, ImageIcon, Loader2, Plus, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import type { FlatDetail } from 'types/api'

import { ImageLightbox } from '@components/image/ImageLightbox'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useConfirm } from '@/components/ui/confirm-dialog'

const MAX_IMAGES = 25

type GalleryImage = { uid: string; url: string }

function mapGallery(gallery?: FlatDetail['gallery']): GalleryImage[] {
  return (gallery ?? [])
    .filter((item) => item.id)
    .map((item) => ({ uid: item.id!, url: SNA_IMAGE_PREFIX + item.id }))
}

function SortableImage({
  img,
  isMain,
  onRemove,
  onOpen
}: {
  img: GalleryImage
  isMain: boolean
  onRemove: (img: GalleryImage) => void
  onOpen: () => void
}) {
  const { t } = useTranslation('flat')
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: img.uid })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative aspect-square overflow-hidden rounded-2xl border border-ink/[0.08] bg-ink/[0.03]"
    >
      <img
        src={img.url}
        alt=""
        loading="lazy"
        onClick={onOpen}
        className="h-full w-full cursor-zoom-in object-cover"
      />

      {isMain && (
        <span className="absolute left-2 top-2 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-white shadow">
          {t('gallery.main')}
        </span>
      )}

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

      <div className="absolute right-2 top-2 flex gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
        <Button
          type="button"
          size="icon"
          aria-label="drag"
          className="h-9 w-9 cursor-grab rounded-full bg-white text-ink hover:bg-white hover:text-primary active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          aria-label="delete"
          onClick={(e) => {
            e.stopPropagation()
            onRemove(img)
          }}
          className="h-9 w-9 rounded-full bg-white text-destructive hover:bg-white hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export function FlatsGallery({ data }: { data?: FlatDetail['gallery'] }) {
  const { t } = useTranslation('flat')
  const { id } = useParams()
  const queryClient = useQueryClient()
  const { confirm, dialog } = useConfirm()

  const [images, setImages] = useState<GalleryImage[]>(() => mapGallery(data))
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null
  )
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadImage = useUploadFlatImage()
  const deleteImage = useDeleteImage()
  const updateOrder = useUpdateGalleryOrder()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const isUploading = progress !== null
  const isFull = images.length >= MAX_IMAGES

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []).filter((f) =>
      f.type.startsWith('image/')
    )
    e.target.value = '' // allow re-picking the same file
    if (!id || picked.length === 0) return

    const room = MAX_IMAGES - images.length
    const queue = picked.slice(0, room)
    if (queue.length === 0) return

    setProgress({ done: 0, total: queue.length })
    // Sequential: await each upload before starting the next so the server
    // never processes concurrent gallery PATCHes (avoids prod race/disorder).
    for (let i = 0; i < queue.length; i++) {
      const file = queue[i]
      if (!file) continue
      try {
        const updated = await uploadImage.mutateAsync({ id, file })
        setImages(mapGallery(updated.gallery))
      } catch {
        // useOnError already surfaced a toast; keep going with the rest.
      }
      setProgress({ done: i + 1, total: queue.length })
    }
    setProgress(null)
    queryClient.invalidateQueries({ queryKey: ['flats'] })
  }

  const handleRemove = async (img: GalleryImage) => {
    if (!id) return
    const ok = await confirm({
      title: t('gallery.delete_confirm'),
      destructive: true,
      confirmText: t('gallery.yes'),
      cancelText: t('gallery.no')
    })
    if (!ok) return
    deleteImage.mutate({
      id,
      params: {
        image_pk: img.uid,
        onSuccess: () =>
          setImages((prev) => prev.filter((x) => x.uid !== img.uid))
      }
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !id) return
    setImages((items) => {
      const oldIndex = items.findIndex((x) => x.uid === active.id)
      const newIndex = items.findIndex((x) => x.uid === over.id)
      const next = arrayMove(items, oldIndex, newIndex)
      updateOrder.mutate({ id, body: { order: next.map((x) => x.uid) } })
      return next
    })
  }

  return (
    <Card className="space-y-5 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 font-display text-xl font-bold tracking-tight text-ink">
            {t('gallery.gallery')}
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('gallery.count', { count: images.length, max: MAX_IMAGES })}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {isUploading && (
            <span className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              {t('gallery.uploading', {
                done: progress?.done,
                total: progress?.total
              })}
            </span>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFiles}
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isFull}
          >
            <Plus className="h-4 w-4" />
            {t('gallery.add_new')}
          </Button>
        </div>
      </div>

      {isFull && (
        <p className="text-sm font-medium text-peach">
          {t('gallery.limit_reached', { max: MAX_IMAGES })}
        </p>
      )}

      {images.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={images.map((x) => x.uid)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {images.map((img, index) => (
                <SortableImage
                  key={img.uid}
                  img={img}
                  isMain={index === 0}
                  onRemove={handleRemove}
                  onOpen={() => setLightboxIndex(index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-ink/20 bg-ink/[0.015] px-6 py-14 text-center transition-colors hover:border-primary/50 hover:bg-primary/[0.03] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ImageIcon className="h-6 w-6" />
          </span>
          <span className="text-sm font-semibold text-ink">
            {t('gallery.empty_title')}
          </span>
          <span className="max-w-sm text-sm text-muted-foreground">
            {t('gallery.empty_hint')}
          </span>
        </button>
      )}

      {lightboxIndex !== null && images.length > 0 && (
        <ImageLightbox
          images={images.map((x) => x.url)}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}

      {dialog}
    </Card>
  )
}
